import type { ActivityParams } from "@/store/slices/activitySlice";
import type {
  Activity,
  Bet,
  Match,
  MatchResult,
  MultiplierConfig,
  Player,
  StatusEvent,
} from "@/types";
import { ActivityVariance } from "@/types";
import type { ActivityId, BetId, MatchId, MatchResultId, PlayerId } from "@/types/branded";

// ── Constants ──────────────────────────────────────────────────

export const STORAGE_KEY = "de-vurige-stand-state";

export const DEFAULT_BANKROLL = 100;

export const DEFAULT_MULTIPLIER_CONFIG: MultiplierConfig = {
  scale: { enabled: true, factor: 25 },
  weight: { enabled: true },
  upset: { enabled: false },
  streak: { enabled: false },
  fatigue: { enabled: false, halfLife: 20 },
  momentum: { enabled: false, factor: 0.05, cap: 0.3 },
  comeback: { enabled: false, threshold: 20, maxBonus: 0.5 },
  consistency: { enabled: false, sigmaThreshold: 6, bonus: 0.15 },
  closeness: { enabled: false, maxDelta: 0.5, bonus: 0.2 },
  variety: { enabled: false, decay: 3, bonus: 0.2 },
  rivalry: { enabled: false, lookback: 10, bonus: 0.25 },
};

/** Maps player-friendly variance labels to TrueSkill beta values */
export const VARIANCE_TO_BETA: Record<ActivityVariance, number> = {
  [ActivityVariance.Low]: 2,
  [ActivityVariance.Medium]: 5,
  [ActivityVariance.High]: 12,
};

// ── Intent types (what components pass to store actions) ───────

export interface RecordMatchIntent {
  activityId: ActivityId;
  teams: PlayerId[][];
  ranks: number[];
}

// ── State shape ────────────────────────────────────────────────

export interface AppState {
  // ─── Data (flat records) ───────────────────────────────────
  players: Record<PlayerId, Player>;
  activities: Record<ActivityId, Activity>;
  matches: Record<MatchId, Match>;
  results: Record<MatchResultId, MatchResult>;
  bets: Record<BetId, Bet>;

  /** Most recent notable event, shown on TV status line */
  lastStatusEvent: StatusEvent | null;

  /** All status events for the current session (news ticker) */
  statusHistory: StatusEvent[];

  /** Admin-configurable scoring multipliers */
  multiplierConfig: MultiplierConfig;

  // ─── Player actions ────────────────────────────────────────
  addPlayer: (name: string) => void;
  renamePlayer: (id: PlayerId, name: string) => void;
  deletePlayer: (id: PlayerId) => void;

  // ─── Activity actions ──────────────────────────────────────
  addActivity: (params: ActivityParams) => void;
  updateActivity: (id: ActivityId, params: ActivityParams) => void;
  deleteActivity: (id: ActivityId) => void;

  // ─── Match actions ─────────────────────────────────────────
  /** Records a match: computes ratings, resolves bets, updates personal bests. */
  recordMatch: (intent: RecordMatchIntent) => void;
  deleteMatch: (matchId: MatchId, resultId: MatchResultId) => void;

  // ─── Betting actions ───────────────────────────────────────
  placeBet: (bet: Bet) => void;
  resolveBetsForActivity: (activityId: ActivityId, winnerId: PlayerId) => void;

  // ─── Config actions ────────────────────────────────────────
  updateMultiplierConfig: (config: Partial<MultiplierConfig>) => void;

  // ─── Bulk actions ──────────────────────────────────────────
  importState: (data: PersistedState) => void;
  resetState: () => void;
  resetScores: () => void;
  /** Replays every match chronologically with current multiplier config. */
  recalculateAll: () => void;

  /** Applies the given persisted state to the store. */
  applyNetworkState: (state: PersistedState) => void;
}

/** The subset of state that gets persisted (no functions). */
export type PersistedState = {
  players: Record<PlayerId, Player>;
  activities: Record<ActivityId, Activity>;
  matches: Record<MatchId, Match>;
  results: Record<MatchResultId, MatchResult>;
  bets: Record<BetId, Bet>;
  lastStatusEvent: StatusEvent | null;
  statusHistory: StatusEvent[];
  multiplierConfig: MultiplierConfig;
};

// ── Shared helpers ─────────────────────────────────────────────

/**
 * Type-safe empty record for branded-key maps.
 * Avoids `{} as Record<BrandedId, T>` casts throughout slices.
 */
export const emptyRecord = <K extends string, V>(): Record<K, V> =>
  Object.create(null) as Record<K, V>;
